import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react"
import { fetchEmpresas, fetchMe, type Empresa, type MeResponse } from "../../lib/api"

type EmpresaContextValue = {
  me: MeResponse | null
  empresas: Empresa[]
  empresaId: string | null
  setEmpresaId: (id: string) => void
  loading: boolean
  reload: () => Promise<void>
}

const EmpresaContext = createContext<EmpresaContextValue | null>(null)

export const EmpresaProvider = ({ children }: { children: ReactNode }) => {
  const [me, setMe] = useState<MeResponse | null>(null)
  const [empresas, setEmpresas] = useState<Empresa[]>([])
  const [empresaId, setEmpresaIdState] = useState<string | null>(() => localStorage.getItem("fiscalhub_empresa_id"))
  const [loading, setLoading] = useState(true)

  const reload = async () => {
    setLoading(true)
    try {
      const meData = await fetchMe()
      setMe(meData)
      if (meData.must_change_password) {
        setEmpresas([])
        return
      }
      const empresasData = await fetchEmpresas()
      setEmpresas(empresasData)
      setEmpresaIdState((current) => {
        if (current && empresasData.some((empresa) => empresa.id === current)) return current
        const next = empresasData[0]?.id ?? null
        if (next) localStorage.setItem("fiscalhub_empresa_id", next)
        else localStorage.removeItem("fiscalhub_empresa_id")
        return next
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    reload()
  }, [])

  const setEmpresaId = (id: string) => {
    setEmpresaIdState(id)
    localStorage.setItem("fiscalhub_empresa_id", id)
  }

  const value = useMemo(
    () => ({ me, empresas, empresaId, setEmpresaId, loading, reload }),
    [me, empresas, empresaId, loading],
  )

  return <EmpresaContext.Provider value={value}>{children}</EmpresaContext.Provider>
}

export const useEmpresa = () => {
  const ctx = useContext(EmpresaContext)
  if (!ctx) {
    throw new Error("useEmpresa fora do provider")
  }
  return ctx
}
